// frontend/src/api/livestockPhotos.ts
/**
 * API client for one organism's photos as the compare page and Line up need
 * them (/api/tanks/{tankId}/livestock/{livestockId}/...). Contract:
 * docs/plans/2026-10-29-livestock-compare-plan.md, 3.7. A photo is a timeline
 * entry with a photo; a note-only entry is "not found" on every route here.
 */

import { api } from './client';
import type { PhotoFraming, PhotoIndex } from '@/types/photos';
import type { OrganismPhoto } from '@/types/timeline';

export interface LivestockReferenceResponse {
  reference_entry_id: number | null;
}

function organismPath(tankId: string, livestockId: number): string {
  return `/api/tanks/${encodeURIComponent(tankId)}/livestock/${livestockId}`;
}

export const livestockPhotosApi = {
  /** Every photo entry as (id, date, thumb, framing), oldest first. */
  index(tankId: string, livestockId: number): Promise<PhotoIndex> {
    return api.get<PhotoIndex>(`${organismPath(tankId, livestockId)}/photos/index`);
  },

  get(tankId: string, livestockId: number, id: number): Promise<OrganismPhoto> {
    return api.get<OrganismPhoto>(`${organismPath(tankId, livestockId)}/timeline/${id}`);
  },

  /** Line a photo up: save its framing against the organism's reference photo. */
  setFraming(
    tankId: string,
    livestockId: number,
    id: number,
    framing: PhotoFraming
  ): Promise<OrganismPhoto> {
    return api.put<OrganismPhoto>(
      `${organismPath(tankId, livestockId)}/timeline/${id}/framing`,
      framing
    );
  },

  /** Reset a photo's line up: it shows as taken again. */
  clearFraming(tankId: string, livestockId: number, id: number): Promise<OrganismPhoto> {
    return api.delete<OrganismPhoto>(`${organismPath(tankId, livestockId)}/timeline/${id}/framing`);
  },

  /** An entry id makes that photo the organism's reference; null leaves it without one. */
  setReference(
    tankId: string,
    livestockId: number,
    entryId: number | null
  ): Promise<LivestockReferenceResponse> {
    return api.put<LivestockReferenceResponse>(`${organismPath(tankId, livestockId)}/reference`, {
      entry_id: entryId,
    });
  },
};
